// client/src/components/layout/LessonLayout.jsx - Updated with Classification Module
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Card } from '../components/ui/UIComponents';
import TheoryPresentation from '../components/activity/TheoryPresentation';
import ConversationSimulation from '../components/activity/ConversationSimulation';
import ClassificationExercise from '../components/activity/ClassificationExercise';
import EvaluationForm from '../components/activity/EvaluationForm';
import FinalMessage from '../components/layout/FinalMessage';
import { LES1_CONFIG, getModuleById, getNextModule } from '../lessons/les1-motiverende-gespreksvoering/config';
import { MODULE1_CONFIG } from '../lessons/les1-motiverende-gespreksvoering/modules/Module1';
import { MODULE2_CONFIG } from '../lessons/les1-motiverende-gespreksvoering/modules/Module2';
import { MODULE3_CONFIG } from '../lessons/les1-motiverende-gespreksvoering/modules/Module3';
import { MODULE4_CONFIG } from '../lessons/les1-motiverende-gespreksvoering/modules/Module4';
import { MODULE5_CONFIG } from '../lessons/les1-motiverende-gespreksvoering/modules/Module5';
import { MODULE6_CONFIG } from '../lessons/les1-motiverende-gespreksvoering/modules/Module6';
import { EXTRA_VERANDERTAAL_CONFIG } from '../lessons/les1-motiverende-gespreksvoering/modules/ExtraVerandertaal';
import { lessonsAPI } from '../services/api';

const LessonLayout = () => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const [moduleProgress, setModuleProgress] = useState(null);
  const [completionStage, setCompletionStage] = useState(null);
  const [evaluationData, setEvaluationData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const currentModule = getModuleById(moduleId);

  useEffect(() => {
    const loadModuleProgress = async () => {
      if (!currentModule) {
        setError('Module niet gevonden');
        return;
      }

      // Reset completion stage on module change
      setCompletionStage(null);

      // For Module 6, check if evaluation is needed based on stored progress
      if (moduleId === 'module6') {
        try {
          const data = await lessonsAPI.getProgress('les1');
          const module6Progress = data.progress?.moduleProgress?.module6;

          // Verify completion by presence of completedAt to match server model
          const moduleIsCompleted = Boolean(module6Progress?.completedAt || module6Progress?.completed);
          // Check evaluatedAt field (not evaluation.completed) to see if evaluation was submitted
          const evaluationCompleted = Boolean(module6Progress?.evaluatedAt);

          if (moduleIsCompleted && !evaluationCompleted) {
            setCompletionStage('evaluation');
          }
          // If both module and evaluation are completed, user can redo the module
          // (completionStage stays null, so ConversationSimulation will render)
        } catch (e) {
          console.error('Error checking evaluation status:', e);
        }
      }
    };

    loadModuleProgress();
  }, [moduleId, currentModule]);

  const handleModuleComplete = async (completionData) => {
    try {
      // Save progress to API
      await lessonsAPI.completeModule(moduleId, completionData, 'les1');
      console.log('Module completed:', completionData);
      
      setModuleProgress(completionData);
      
      // Special handling for Module 6 - show evaluation form
      if (moduleId === 'module6') {
        setCompletionStage('evaluation');
      } else {
        // Standard completion for other modules
        setCompletionStage('completed');
        
        // Auto-navigate to lesson dashboard for all modules
        setTimeout(() => {
          navigate('/les1');
        }, currentModule.isOptional ? 5000 : 3000);
      }
      
    } catch (err) {
      setError('Kon voortgang niet opslaan');
      console.error('Error saving module completion:', err);
    }
  };

  const handleEvaluationSubmit = async (formData) => {
    try {
      setIsLoading(true);
      await lessonsAPI.submitEvaluation(moduleId, formData, 'les1');
      console.log('✅ Evaluation submitted');
      
      // Save evaluation data for final message
      setEvaluationData(formData);
      setCompletionStage('final');
    } catch (err) {
      setError('Kon evaluatie niet opslaan');
      console.error('Error submitting evaluation:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const handleFinalMessageDismiss = async (newsletterUpdate) => {
    // If newsletter preference changed, update evaluation
    if (newsletterUpdate !== null) {
      try {
        await lessonsAPI.updateNewsletterPreference(moduleId, newsletterUpdate, 'les1');
        console.log('✅ Newsletter preference updated:', newsletterUpdate);
      } catch (error) {
        console.error('Error updating newsletter preference:', error);
      }
    }
    
    navigate('/les1');
  };

  const handleProgressUpdate = (progressData) => {
    setModuleProgress(progressData);
  };

  const handleBackToDashboard = () => {
    navigate('/les1');
  };

  const renderModuleContent = () => {
    if (!currentModule) return null;

    switch (currentModule.type) {
      case 'theory':
        if (moduleId === 'module1') {
          return (
            <TheoryPresentation
              theoryConfig={MODULE1_CONFIG.theory}
              onComplete={handleModuleComplete}
              onProgress={handleProgressUpdate}
              isCompleted={completionStage === 'completed'}
            />
          );
        }
        break;
        
      case 'conversation':
        // Show ConversationSimulation for conversation modules
        if (moduleId === 'module2') {
          return (
            <ConversationSimulation
              moduleConfig={MODULE2_CONFIG}
              onComplete={handleModuleComplete}
              onProgress={handleProgressUpdate}
            />
          );
        }

        if (moduleId === 'module3') {
          return (
            <ConversationSimulation
              moduleConfig={MODULE3_CONFIG}
              onComplete={handleModuleComplete}
              onProgress={handleProgressUpdate}
            />
          );
        }

        if (moduleId === 'module4') {
          return (
            <ConversationSimulation
              moduleConfig={MODULE4_CONFIG}
              onComplete={handleModuleComplete}
              onProgress={handleProgressUpdate}
            />
          );
        }

        if (moduleId === 'module5') {
          return (
            <ConversationSimulation
              moduleConfig={MODULE5_CONFIG}
              onComplete={handleModuleComplete}
              onProgress={handleProgressUpdate}
            />
          );
        }

        if (moduleId === 'module6') {
          return (
            <ConversationSimulation
              moduleConfig={MODULE6_CONFIG}
              onComplete={handleModuleComplete}
              onProgress={handleProgressUpdate}
            />
          );
        }
        
        // Fallback for other conversation modules
        return (
          <Card className="text-center p-8">
            <div className="text-6xl mb-4">🚧</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Gesprekstraining in Ontwikkeling
            </h2>
            <p className="text-gray-600 mb-6">
              Deze module wordt momenteel ontwikkeld.
            </p>
            <Button onClick={handleBackToDashboard}>
              Terug naar Dashboard
            </Button>
          </Card>
        );
        
      case 'classification':
        // NEW: Handle classification modules
        if (moduleId === 'extra-verandertaal') {
          return (
            <ClassificationExercise
              moduleConfig={EXTRA_VERANDERTAAL_CONFIG}
              onComplete={handleModuleComplete}
              onProgress={handleProgressUpdate}
            />
          );
        }
        
        // Fallback for other classification modules
        return (
          <Card className="text-center p-8">
            <div className="text-6xl mb-4">🔍</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Classificatieoefening in Ontwikkeling
            </h2>
            <p className="text-gray-600 mb-6">
              Deze module wordt momenteel ontwikkeld.
            </p>
            <Button onClick={handleBackToDashboard}>
              Terug naar Dashboard
            </Button>
          </Card>
        );
        
      case 'assessment':
        // TODO: Implement assessment component
        return (
          <Card className="text-center p-8">
            <div className="text-6xl mb-4">📋</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Integratie & Toets
            </h2>
            <p className="text-gray-600 mb-6">
              Deze module wordt momenteel ontwikkeld. Hier kun je binnenkort je 
              vaardigheden testen in een uitgebreide evaluatie.
            </p>
            <Button onClick={handleBackToDashboard}>
              Terug naar Dashboard
            </Button>
          </Card>
        );
        
      default:
        return (
          <Card className="text-center p-8">
            <div className="text-6xl mb-4">❓</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Onbekend Module Type
            </h2>
            <p className="text-gray-600 mb-6">
              Dit module type wordt niet herkend: {currentModule.type}
            </p>
            <Button onClick={handleBackToDashboard}>
              Terug naar Dashboard
            </Button>
          </Card>
        );
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <Card className="text-center max-w-md">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Er is een fout opgetreden</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={handleBackToDashboard}>
            Terug naar Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <Card className="text-center max-w-md">
          <div className="text-gray-500 text-5xl mb-4">🔍</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Module niet gevonden</h2>
          <p className="text-gray-600 mb-4">De opgevraagde module bestaat niet.</p>
          <Button onClick={handleBackToDashboard}>
            Terug naar Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-cream-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                onClick={handleBackToDashboard}
                className="text-charcoal-600 hover:text-charcoal-800"
              >
                ← Dashboard
              </Button>
              <div>
                <h1 className="font-header text-xl font-medium text-charcoal-800">
                  {currentModule.title}
                </h1>
                <p className="text-sm text-charcoal-600 font-body">
                  {currentModule.description}
                </p>
              </div>
            </div>
            
          </div>
        </div>
      </div>

      {/* Main Content */}
      {currentModule.type === 'conversation' || currentModule.type === 'classification' ? (
        // For conversation and classification modules, give full screen space without extra containers
        <div className="w-full min-h-screen">
          {renderModuleContent()}
        </div>
      ) : (
        // For other modules (theory, etc), keep the container
        <div className="container mx-auto px-4 py-8">
          {renderModuleContent()}
        </div>
      )}

      {/* Evaluation Form - Module 6 only */}
      {completionStage === 'evaluation' && (
        <EvaluationForm onSubmit={handleEvaluationSubmit} />
      )}

      {/* Final Message - Module 6 only */}
      {completionStage === 'final' && (
        <FinalMessage 
          onDismiss={handleFinalMessageDismiss}
          evaluationData={evaluationData}
        />
      )}

      {/* Completion Modal/Message */}
      {completionStage === 'completed' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="text-center max-w-md">
            <div className="text-green-500 text-5xl mb-4">🎉</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Module Voltooid!
            </h2>
            <p className="text-gray-600 mb-6">
              Je hebt {currentModule.title} succesvol afgerond.
            </p>
            
            <div>
              <p className="text-sm text-gray-500 mb-4">
                Je wordt automatisch teruggeleid naar het dashboard.
              </p>
              <Button onClick={handleBackToDashboard}>
                Terug naar Dashboard
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default LessonLayout;